/**
 * The closed widget. The agent's face is its one symbol: one modality reads as a
 * contact (face, label, what it opens); both are the face, "Call" and "Write".
 */

import { LitElement, css, html } from "lit";
import { customElement, property } from "lit/decorators.js";

import "./deepagent-avatar.js";

import type { WidgetSettingsPayload } from "../state/api-client.js";

export type LauncherAction = "open" | "call" | "chat";

@customElement("deepagent-launcher")
export class DeepAgentLauncher extends LitElement {
  /**
   * Lets `launcher.focus()` land on the first button inside the shadow root,
   * so closing the panel can put focus back where the visitor left it.
   */
  static override shadowRootOptions = {
    ...LitElement.shadowRootOptions,
    delegatesFocus: true,
  };

  static override styles = css`
    :host {
      display: inline-block;
      max-width: calc(100vw - 32px);
    }

    .bar {
      display: inline-flex;
      align-items: center;
      max-width: 100%;
      box-sizing: border-box;
      gap: 6px;
      padding: 6px;
      background: var(--da-bg);
      border: 1px solid var(--da-border);
      border-radius: var(--da-radius-lg, 2px);
      box-shadow: var(--da-shadow-panel);
      animation: da-launcher-in 260ms cubic-bezier(0.16, 1, 0.3, 1) both;
    }

    button {
      appearance: none;
      border: none;
      margin: 0;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      height: 44px;
      border-radius: var(--da-radius, 2px);
      font-family: inherit;
      white-space: nowrap;
      -webkit-tap-highlight-color: transparent;
      transition:
        background-color 180ms cubic-bezier(0.16, 1, 0.3, 1),
        transform 180ms cubic-bezier(0.16, 1, 0.3, 1);
    }

    button:active {
      transform: scale(0.98);
      transition-duration: 90ms;
    }

    button:focus-visible {
      outline: 2px solid var(--da-accent);
      outline-offset: 2px;
    }

    deepagent-avatar {
      --da-avatar-size: 44px;
    }

    .contact {
      min-width: 0;
      max-width: 100%;
      gap: 12px;
      padding: 0 14px 0 0;
      background: transparent;
      color: var(--da-text);
      text-align: left;
    }

    @media (hover: hover) {
      .contact:hover {
        background: var(--da-surface-2);
      }
    }

    .both .contact {
      padding: 0;
    }

    .text {
      display: flex;
      flex-direction: column;
      gap: 1px;
      min-width: 0;
    }

    .label,
    .kind {
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .label {
      font-family: var(--da-font-display);
      font-size: 15px;
      font-weight: 600;
      letter-spacing: -0.015em;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .kind {
      font-size: 12px;
      color: var(--da-text-muted);
    }

    .action {
      padding: 0 18px;
      font-size: 14px;
      font-weight: 600;
      letter-spacing: -0.005em;
    }

    .primary {
      background: var(--da-accent);
      color: var(--da-accent-fg);
    }

    @media (hover: hover) {
      .primary:hover {
        background: color-mix(in srgb, var(--da-accent) 88%, #000);
      }
    }

    .secondary {
      background: color-mix(in srgb, var(--da-accent) 12%, transparent);
      color: var(--da-text);
    }

    @media (hover: hover) {
      .secondary:hover {
        background: color-mix(in srgb, var(--da-accent) 18%, transparent);
      }
    }

    @keyframes da-launcher-in {
      from { transform: translateY(6px); opacity: 0; }
      to   { transform: translateY(0);   opacity: 1; }
    }

    @media (prefers-reduced-motion: reduce) {
      .bar { animation: none; }
    }
  `;

  @property({ type: String }) label = "";
  @property({ type: String }) avatarUrl: string | null = null;
  @property({ type: String }) modality: WidgetSettingsPayload["modality"] =
    "voice";
  @property({ type: String }) callLabel = "";
  @property({ type: String }) chatLabel = "";
  /** Under the label of a one-modality launcher: "Voice call" or "Chat". */
  @property({ type: String }) kindLabel = "";

  /** Where the agent's face sits, for the panel's figure to fly from. */
  faceRect(): DOMRect | null {
    return this.renderRoot.querySelector("deepagent-avatar")?.getBoundingClientRect() ?? null;
  }

  private emit(action: LauncherAction) {
    this.dispatchEvent(
      new CustomEvent<LauncherAction>("launcher-action", {
        detail: action,
        bubbles: true,
        composed: true,
      }),
    );
  }

  override render() {
    const face = html`<deepagent-avatar .url=${this.avatarUrl}></deepagent-avatar>`;
    if (this.modality !== "voice_text") {
      return html`<div class="bar">
        <button
          type="button"
          class="contact"
          aria-label=${`${this.label} · ${this.kindLabel}`}
          @click=${() => this.emit("open")}
        >
          ${face}
          <span class="text">
            <span class="label">${this.label}</span>
            <span class="kind">${this.kindLabel}</span>
          </span>
        </button>
      </div>`;
    }
    return html`<div class="bar both" role="group" aria-label=${this.label}>
      <button
        type="button"
        class="contact"
        aria-label=${this.label}
        @click=${() => this.emit("open")}
      >
        ${face}
      </button>
      <button type="button" class="action primary" @click=${() => this.emit("call")}>
        ${this.callLabel}
      </button>
      <button type="button" class="action secondary" @click=${() => this.emit("chat")}>
        ${this.chatLabel}
      </button>
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "deepagent-launcher": DeepAgentLauncher;
  }
}
